import React, { useEffect, useState } from "react";
import api, { money, formatErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Eye, Trash, Download } from "@phosphor-icons/react";
import { toast } from "sonner";
import { exportToCSV } from "@/lib/csvExport";

const TXN_TYPES = [
  { value: "ADVANCE_PAID", label: "Advance Paid to Technician", nature: "receivable" },
  { value: "FIELD_CASH", label: "Cash Collected in Field", nature: "receivable" },
  { value: "COMMISSION", label: "Commission Earned", nature: "payable" },
  { value: "SALARY", label: "Salary/Wage Earned", nature: "payable" },
  { value: "DEPOSIT", label: "Cash Deposited by Technician", nature: "credit" },
  { value: "PAYOUT", label: "Payment Made to Technician", nature: "debit" },
];

function typeBadge(t) {
  const map = {
    ADVANCE_PAID: "bg-orange-100 text-orange-700",
    FIELD_CASH: "bg-yellow-100 text-yellow-700",
    COMMISSION: "bg-green-100 text-green-700",
    SALARY: "bg-green-100 text-green-700",
    DEPOSIT: "bg-blue-100 text-blue-700",
    PAYOUT: "bg-purple-100 text-purple-700",
  };
  return map[t] || "bg-secondary";
}

export default function TechnicianLedger() {
  const [summary, setSummary] = useState([]);
  const [detailFor, setDetailFor] = useState(null);
  const [detail, setDetail] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({
    technician_id: "", date: new Date().toISOString().slice(0, 10),
    txn_type: "ADVANCE_PAID", amount: 0, payment_mode: "CASH", reference: "", remarks: "",
  });

  const load = async () => {
    const { data } = await api.get("/technicians/ledger-summary");
    setSummary(data);
  };
  useEffect(() => { load(); }, []);

  const openDetail = async (row) => {
    setDetailFor(row);
    const { data } = await api.get(`/technicians/${row.technician_id}/ledger`);
    setDetail(data);
  };

  const openAdd = (tid) => {
    setForm({ ...form, technician_id: tid || "", date: new Date().toISOString().slice(0, 10) });
    setAddOpen(true);
  };

  const submit = async () => {
    if (!form.technician_id) return toast.error("Select technician");
    if (!form.amount || parseFloat(form.amount) <= 0) return toast.error("Amount must be positive");
    try {
      await api.post("/technician-transactions", { ...form, amount: parseFloat(form.amount) });
      toast.success("Transaction recorded");
      setAddOpen(false);
      load();
      if (detailFor) openDetail(detailFor);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this transaction?")) return;
    try {
      await api.delete(`/technician-transactions/${id}`);
      toast.success("Deleted");
      openDetail(detailFor);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const downloadCollectionReport = async () => {
    try {
      const { data } = await api.get("/technicians/ledger-export");
      if (!data.length) return toast.error("No transactions to export");
      const cols = Object.keys(data[0]).map(k => ({ label: k, key: k }));
      exportToCSV(data, cols, "technician_collection_report");
      toast.success(`Downloaded ${data.length} entries`);
    } catch (e) { toast.error(formatErr(e)); }
  };

  // Aggregate KPIs across all technicians
  const kpi = summary.reduce((acc, s) => {
    acc.commission += s.commission_earned || 0;
    acc.cashHeld += s.cash_held || 0;
    acc.advances += s.advance_paid || 0;
    acc.netPayable += Math.max(0, s.net || 0);
    acc.netReceivable += Math.max(0, -(s.net || 0));
    return acc;
  }, { commission: 0, cashHeld: 0, advances: 0, netPayable: 0, netReceivable: 0 });

  return (
    <div className="space-y-4" data-testid="tech-ledger-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold">Technician Ledger</h1>
          <p className="text-sm text-muted-foreground mt-1">Advance, commission (parts × cash only), field cash & settlement — auto-tracked from sales.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={downloadCollectionReport} data-testid="tech-ledger-download">
            <Download size={14} className="mr-1" /> Collection Report (CSV)
          </Button>
          <Button onClick={() => openAdd(null)} data-testid="tech-txn-add" className="bg-[hsl(24.6_95%_53.1%)] hover:bg-[hsl(24.6_95%_47%)] text-white">
            <Plus size={16} className="mr-1" weight="bold" /> New Transaction
          </Button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className="p-4 border border-border">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Total Commission</div>
          <div className="font-display text-xl font-bold text-green-700 mt-1 tabular-nums">{money(kpi.commission)}</div>
          <div className="text-[10px] text-muted-foreground mt-1">Parts sales · cash only</div>
        </Card>
        <Card className="p-4 border border-border">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Cash In Field</div>
          <div className="font-display text-xl font-bold text-yellow-700 mt-1 tabular-nums">{money(kpi.cashHeld)}</div>
          <div className="text-[10px] text-muted-foreground mt-1">With technicians</div>
        </Card>
        <Card className="p-4 border border-border">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Advances Outstanding</div>
          <div className="font-display text-xl font-bold text-orange-700 mt-1 tabular-nums">{money(kpi.advances)}</div>
          <div className="text-[10px] text-muted-foreground mt-1">To be recovered</div>
        </Card>
        <Card className="p-4 border border-green-200 bg-green-50/50">
          <div className="text-[10px] uppercase tracking-wider text-green-700 font-semibold">To Pay (Total)</div>
          <div className="font-display text-xl font-bold text-green-800 mt-1 tabular-nums">{money(kpi.netPayable)}</div>
          <div className="text-[10px] text-green-700 mt-1">Across all technicians</div>
        </Card>
        <Card className="p-4 border border-orange-200 bg-orange-50/50">
          <div className="text-[10px] uppercase tracking-wider text-orange-700 font-semibold">To Collect (Total)</div>
          <div className="font-display text-xl font-bold text-orange-800 mt-1 tabular-nums">{money(kpi.netReceivable)}</div>
          <div className="text-[10px] text-orange-700 mt-1">Across all technicians</div>
        </Card>
      </div>

      <Card className="p-0 border border-border overflow-hidden">
        <div className="p-4 border-b border-border font-display font-semibold flex items-center justify-between">
          <span>Balance Summary — Pay / Collect per Technician</span>
          <span className="text-xs text-muted-foreground font-normal">Commission on cash-parts only · online payments credited directly to company</span>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Technician</TableHead>
              <TableHead className="text-right">Commission Earned</TableHead>
              <TableHead className="text-right">Advances + Cash Held</TableHead>
              <TableHead className="text-right">Final Settlement</TableHead>
              <TableHead className="text-right w-24">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {summary.map(s => (
              <TableRow key={s.technician_id}>
                <TableCell>
                  <div className="font-semibold">{s.technician_name}</div>
                  <div className="text-xs text-muted-foreground">{s.mobile}</div>
                </TableCell>
                <TableCell className="text-right tabular-nums text-green-700">
                  {money(s.we_owe)}
                  <div className="text-[10px] font-normal text-muted-foreground">Commission {money(s.commission_earned)} + Salary {money(s.salary_earned)}</div>
                </TableCell>
                <TableCell className="text-right tabular-nums text-orange-700">
                  {money(s.he_owes)}
                  <div className="text-[10px] font-normal text-muted-foreground">Adv {money(s.advance_paid)} + Cash {money(s.cash_held)} + Payouts {money(s.payouts_made)}</div>
                </TableCell>
                <TableCell className={`text-right tabular-nums font-bold ${s.net >= 0 ? "text-green-700" : "text-orange-700"}`}>
                  {s.net >= 0 ? `+${money(s.net)}` : money(s.net)}
                  <div className="text-[10px] font-semibold uppercase tracking-wide">
                    {s.net > 0 ? "PAY TO TECH" : s.net < 0 ? "COLLECT FROM TECH" : "SETTLED"}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <button onClick={() => openDetail(s)} data-testid={`tech-ledger-view-${s.technician_id}`} className="p-1.5 rounded hover:bg-secondary mr-1"><Eye size={16} /></button>
                  <button onClick={() => openAdd(s.technician_id)} className="p-1.5 rounded hover:bg-secondary text-[hsl(24.6_95%_53.1%)]"><Plus size={16} /></button>
                </TableCell>
              </TableRow>
            ))}
            {!summary.length && <TableRow><TableCell colSpan={5} className="text-center py-12 text-muted-foreground">No technicians yet. Add some in Technicians master.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      {/* Detail modal */}
      <Dialog open={!!detailFor} onOpenChange={(v) => { if (!v) { setDetailFor(null); setDetail(null); } }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">{detailFor?.technician_name} — Ledger</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4">
              {/* Two-column: what tech owes us vs what we owe tech */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="p-4 border-orange-200 bg-orange-50/40">
                  <div className="text-xs uppercase tracking-wider text-orange-700 font-semibold mb-2">🟠 Technician se lena hai (He owes us)</div>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between"><span>Advances Paid</span><span className="tabular-nums">{money(detail.advance_paid)}</span></div>
                    <div className="flex justify-between"><span>Cash Held (Field)</span><span className="tabular-nums">{money(detail.cash_held)}</span></div>
                    <div className="flex justify-between"><span>Extra Payouts</span><span className="tabular-nums">{money(detail.payouts_made)}</span></div>
                    <div className="flex justify-between border-t pt-1 mt-1 font-bold text-orange-800">
                      <span>Total Receivable</span><span className="tabular-nums">{money(detail.he_owes)}</span>
                    </div>
                  </div>
                </Card>
                <Card className="p-4 border-green-200 bg-green-50/40">
                  <div className="text-xs uppercase tracking-wider text-green-700 font-semibold mb-2">🟢 Technician ko dena hai (We owe him)</div>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between"><span>Commission (parts only)</span><span className="tabular-nums">{money(detail.commission_earned)}</span></div>
                    <div className="flex justify-between"><span>Salary / Wages</span><span className="tabular-nums">{money(detail.salary_earned)}</span></div>
                    <div className="flex justify-between border-t pt-1 mt-1 font-bold text-green-800">
                      <span>Total Payable</span><span className="tabular-nums">{money(detail.we_owe)}</span>
                    </div>
                  </div>
                </Card>
              </div>
              <Card className={`p-5 text-center ${detail.net >= 0 ? "bg-green-100 border-green-300" : "bg-orange-100 border-orange-300"}`}>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">Final Settlement</div>
                <div className={`font-display text-4xl font-bold ${detail.net >= 0 ? "text-green-800" : "text-orange-800"}`}>
                  ₹ {money(Math.abs(detail.net))}
                </div>
                <div className="text-sm font-semibold uppercase tracking-wide mt-1">
                  {detail.net > 0 ? `💸 Pay ${money(detail.net)} to ${detailFor?.technician_name}`
                   : detail.net < 0 ? `💰 Collect ${money(Math.abs(detail.net))} from ${detailFor?.technician_name}`
                   : `✓ Settled — no dues`}
                </div>
                <div className="text-[11px] text-muted-foreground mt-2">
                  Formula: (Commission + Salary) − (Advances + Cash Held + Payouts) = {money(detail.we_owe)} − {money(detail.he_owes)} = {money(detail.net)}
                </div>
              </Card>
              <div className="border border-border rounded overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Mode</TableHead>
                      <TableHead>Ref</TableHead>
                      <TableHead>Remarks</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="text-right">Running Net</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(detail.transactions || []).map(tx => (
                      <TableRow key={tx.id}>
                        <TableCell className="text-xs">{tx.date}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-semibold ${typeBadge(tx.txn_type)}`}>
                            {tx.txn_type.replace(/_/g, " ")}
                          </span>
                        </TableCell>
                        <TableCell className="text-xs">{tx.payment_mode}</TableCell>
                        <TableCell className="font-mono text-xs">{tx.reference || "-"}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{tx.remarks}</TableCell>
                        <TableCell className="text-right tabular-nums font-semibold">{money(tx.amount)}</TableCell>
                        <TableCell className={`text-right tabular-nums text-xs ${tx.running_net >= 0 ? "text-green-700" : "text-orange-700"}`}>
                          {tx.running_net >= 0 ? `+${money(tx.running_net)}` : money(tx.running_net)}
                        </TableCell>
                        <TableCell><button onClick={() => remove(tx.id)} className="p-1 text-destructive hover:bg-destructive/10 rounded"><Trash size={13} /></button></TableCell>
                      </TableRow>
                    ))}
                    {!detail.transactions?.length && <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No transactions.</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add transaction dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">New Technician Transaction</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Technician*</Label>
              <select value={form.technician_id} onChange={e => setForm({ ...form, technician_id: e.target.value })}
                className="w-full h-10 px-3 border border-border rounded-md bg-card text-sm" data-testid="tech-txn-tech">
                <option value="">Select</option>
                {summary.map(s => <option key={s.technician_id} value={s.technician_id}>{s.technician_name}</option>)}
              </select>
            </div>
            <div>
              <Label>Date*</Label>
              <Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} />
            </div>
            <div>
              <Label>Transaction Type*</Label>
              <select value={form.txn_type} onChange={e => setForm({ ...form, txn_type: e.target.value })}
                className="w-full h-10 px-3 border border-border rounded-md bg-card text-sm" data-testid="tech-txn-type">
                {TXN_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <Label>Payment Mode</Label>
              <select value={form.payment_mode} onChange={e => setForm({ ...form, payment_mode: e.target.value })}
                className="w-full h-10 px-3 border border-border rounded-md bg-card text-sm">
                <option value="CASH">Cash</option>
                <option value="ONLINE">Online / Bank</option>
              </select>
            </div>
            <div>
              <Label>Amount (₹)*</Label>
              <Input type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} data-testid="tech-txn-amount" />
            </div>
            <div>
              <Label>Reference / Call ID</Label>
              <Input value={form.reference} onChange={e => setForm({ ...form, reference: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label>Remarks</Label>
              <Input value={form.remarks} onChange={e => setForm({ ...form, remarks: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={submit} data-testid="tech-txn-save" className="bg-[hsl(24.6_95%_53.1%)] hover:bg-[hsl(24.6_95%_47%)] text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
